import { api } from '../lib/api.js';

/**
 * Letters are REQUESTED and DECIDED, not downloaded.
 *
 * The old client called `GET /api/letters?kind=…` and got a PDF back. That
 * endpoint is gone: a salary certificate is a statement on company letterhead
 * that a bank or a visa office acts on, so somebody with authority over the
 * employee has to agree to it first. The document exists only for an APPROVED
 * request, and only at its own URL.
 */

export type LetterKind = 'salary_certificate' | 'noc' | 'experience';
export type LetterLanguage = 'en' | 'ar';
export type LetterRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface LetterRequestRow {
  id: string;
  employeeId: string;
  kind: LetterKind;
  language: LetterLanguage;
  purpose: string | null;
  status: LetterRequestStatus;
  /** Printed on the document; what a bank quotes when it rings up. */
  reference: string;
  /** Why it was refused. Present only on REJECTED. */
  decisionNote: string | null;
  decidedAt: string | null;
  /** How many PDFs have been produced from this approved request. */
  issuedCount: number;
  lastIssuedAt: string | null;
  createdAt: string;
  employee: {
    firstName: string;
    lastName: string;
    displayName: string | null;
    employeeCode: string | null;
  } | null;
}

export interface SubmitLetterRequestInput {
  kind: LetterKind;
  language: LetterLanguage;
  purpose?: string;
  /** ADMIN/HR only. Anyone else is pinned to themselves by the server. */
  employeeId?: string;
}

/**
 * ONE KEY FOR THE APPROVER'S QUEUE, so every reader of it is the same cache
 * entry: the panel that draws the rows, the bar above it that counts them, and
 * the dashboard's "Needs a decision" card. A second key would be a second
 * request and a second number, and rule 3.2 is about the second number.
 */
export const letterQueueQueryKey = (status: LetterRequestStatus) =>
  ['letter-requests', 'queue', status] as const;

export const lettersApi = {
  async submit(input: SubmitLetterRequestInput): Promise<{
    id: string;
    status: LetterRequestStatus;
    reference: string;
  }> {
    // `api.post` returns the response BODY, not an axios response — so the
    // envelope's `data` is the payload and there is only one level to unwrap.
    const body = await api.post<{
      data: { id: string; status: LetterRequestStatus; reference: string };
    }>('/letters/requests', input);
    return body.data;
  },

  /**
   * The approver's queue, or a person's own history.
   *
   * `employeeId` is honoured only for ADMIN/HR; the server pins everyone else
   * to their own record regardless of what is asked for, so this filter is a
   * convenience and never the access control.
   */
  /*
   * RETURNS THE ROWS AND THE COUNT, not just the rows. The endpoint caps at 200
   * and used to return a bare array, so every caller counted what it was handed
   * and called that the total — see the note on `LETTER_QUEUE_CAP`. `total` is
   * what matches the filter; `rows` is the page of it that came back.
   */
  async list(filter: { status?: LetterRequestStatus; employeeId?: string } = {}): Promise<{
    rows: LetterRequestRow[];
    total: number;
    cap: number;
  }> {
    const params = new URLSearchParams();
    if (filter.status) params.set('status', filter.status);
    if (filter.employeeId) params.set('employeeId', filter.employeeId);
    const query = params.toString();
    const body = await api.get<{
      data: LetterRequestRow[];
      meta?: { total?: number; cap?: number };
    }>(`/letters/requests${query ? `?${query}` : ''}`);
    const rows = body.data;
    return {
      rows,
      // An older API that predates the envelope sends no meta. Falling back to
      // the row count is the only honest reading there: it is what we can see.
      total: body.meta?.total ?? rows.length,
      cap: body.meta?.cap ?? rows.length,
    };
  },

  async decide(
    id: string,
    decision: 'APPROVED' | 'REJECTED',
    note?: string
  ): Promise<void> {
    await api.patch(`/letters/requests/${id}`, { decision, note });
  },

  /**
   * Where the PDF lives. A path rather than a fetch: the caller hands it to
   * `downloadAuthenticated`, which is the one place in the app that attaches
   * the bearer token to a file download.
   */
  pdfPath(id: string): string {
    return `/api/letters/requests/${id}/pdf`;
  },
};
